<template>
  <div>
    <v-dialog
      :value="value"
      max-width="900"
      scrollable
      persistent
      @input="$emit('input', $event)"
    >
      <v-card>
        <v-card-title class="booking-dialog-title">
          <div>
            <h2>Old Mutual Will Collection Booking Form</h2>
            <p v-if="formPrefill" class="booking-dialog-subtitle mb-0">
              {{ formPrefill.fullName }}
            </p>
          </div>
          <v-spacer />
          <v-btn icon aria-label="Close" @click="close">
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div v-if="$apollo.loading" class="text-center py-10">
            <v-progress-circular indeterminate color="primary" />
            <p class="mt-4">Loading details...</p>
          </div>
          <WillCollectionBookingForm
            v-else-if="formPrefill"
            :testator="formPrefill"
            :booking-mode="mode"
            :show-preview-inline="false"
            :show-existing-collection-inline="false"
            @booking-success="handleBookingSuccess"
            @existing-collection="showExistingCollection"
            @close="close"
          />
          <v-alert
            v-else-if="mode === 'testator' && testator"
            type="warning"
            text
            class="my-6"
          >
            {{ blockedReason }}
          </v-alert>
          <p v-else class="text-center py-10">
            We could not load the details for this booking. Please close this
            window and try again.
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!--
      Only one dialog may be open at a time in this app (see
      whenNoDialogIsActive), so this is a sibling of the form dialog rather
      than a child of it: the form dialog closes before this one opens and
      reopens on "Change address". Same reason the dry-run preview is hoisted
      out of the form.
    -->
    <ExistingCollectionBookingDialog
      v-model="existingCollectionOpen"
      :booking="existingCollection"
      :mode="mode"
      :booking-testator-id="bookingTestatorId"
      :testator-name="testator ? testator.fullName : ''"
      :preselected-testator-ids="existingCollectionTestatorIds"
      @linked="handleLinked"
      @input="onExistingCollectionToggle"
    />
  </div>
</template>

<script>
  import { mdiClose } from '@mdi/js';
  import WillCollectionBookingForm from './WillCollectionBookingForm.vue';
  import ExistingCollectionBookingDialog from './ExistingCollectionBookingDialog.vue';
  import { TESTATOR_QUERY } from '../../../constants/graphql/testators';
  import { USER_CONTACT_QUERY } from '../../../constants/graphql/users';
  import { latestWillVersion } from '../../../constants/js/will-verification';
  import { whenNoDialogIsActive } from '../../../helpers/dialogStacking';

  export default {
    name: 'WillCollectionBookingDialog',
    components: { WillCollectionBookingForm, ExistingCollectionBookingDialog },
    props: {
      value: {
        type: Boolean,
        default: false,
      },
      // Named to avoid the app-wide global-mixin computed `testatorId`, which
      // resolves to the LOGGED-IN user's own testator id and silently shadows
      // a same-named prop - an adviser opening this dialog would book for
      // themselves instead of the row's testator.
      bookingTestatorId: {
        type: [String, Number],
        default: null,
      },
      // 'testator' books for bookingTestatorId; 'advisor' books every
      // validated will the logged-in user holds, prefilled with their own
      // contact details.
      mode: {
        type: String,
        default: 'testator',
      },
    },
    data() {
      return {
        icons: { mdiClose },
        testator: null,
        advisorContact: null,
        existingCollection: null,
        existingCollectionTestatorIds: [],
        existingCollectionOpen: false,
        // Set when the existing-collection dialog closes because wills were
        // added, so that close is not mistaken for "Change address".
        linkedFromExisting: false,
      };
    },
    computed: {
      formPrefill() {
        if (this.mode === 'advisor') {
          if (!this.advisorContact) return null;
          return {
            id: null,
            fullName: this.advisorContact.fullName,
            email: this.advisorContact.email,
            cellNumber: this.advisorContact.phone,
            cellCountryCode: 'ZA',
            alternativeCellNumber: null,
            altCellCountryCode: 'ZA',
          };
        }
        return this.canBookCollection ? this.testator : null;
      },
      latestVersion() {
        return this.testator
          ? latestWillVersion(this.testator.willVersions || [])
          : null;
      },
      canBookCollection() {
        return !!(
          this.value &&
          this.testator &&
          this.latestVersion &&
          this.latestVersion.status === 'VALID'
        );
      },
      blockedReason() {
        if (!this.latestVersion) {
          return 'This testator does not have a will version yet, so a collection cannot be booked. The dashboard row may be out of date - it is rebuilt overnight.';
        }
        return `This testator's will has not been validated yet (status: ${this.latestVersion.status}), so a collection cannot be booked. Validate the uploaded will first.`;
      },
    },
    watch: {
      bookingTestatorId() {
        this.testator = null;
      },
    },
    methods: {
      close() {
        this.$emit('input', false);
      },
      handleBookingSuccess(result) {
        this.$emit('booking-success', result);
        this.close();
      },
      // The form found a pickup already booked at the entered address. Swap
      // the form dialog for the existing-collection dialog; the form stays
      // mounted, so its entries survive a "Change address".
      showExistingCollection({ booking, testatorIds }) {
        this.existingCollection = booking;
        this.existingCollectionTestatorIds = testatorIds || [];
        this.$emit('input', false);
        whenNoDialogIsActive(() => {
          this.existingCollectionOpen = true;
        });
      },
      onExistingCollectionToggle(open) {
        if (open) return;
        if (this.linkedFromExisting) {
          this.linkedFromExisting = false;
          return;
        }
        // "Change address" or the close icon: back to the form.
        whenNoDialogIsActive(() => this.$emit('input', true));
      },
      handleLinked({ linkedTestatorIds, errors }) {
        this.linkedFromExisting = true;
        if (this.mode === 'testator') this.evictTestatorFromCache();
        this.$emit('booking-success', {
          dryRun: false,
          linked: true,
          bookedTestatorIds: linkedTestatorIds,
          errors: errors || [],
        });
      },
      // So the overview re-reads the testator (and now sees the booking)
      // instead of serving the cached, pre-booking copy.
      evictTestatorFromCache() {
        try {
          if (
            this.$apollo &&
            this.$apollo.client &&
            this.$apollo.client.cache
          ) {
            this.$apollo.client.cache.evict({
              fieldName: 'testator',
              args: { id: this.bookingTestatorId },
            });
            this.$apollo.client.cache.gc();
          }
        } catch (error) {
          console.warn('Failed to clear Apollo cache:', error);
        }
      },
    },
    apollo: {
      testator: {
        query: TESTATOR_QUERY,
        variables() {
          return {
            id: this.bookingTestatorId,
          };
        },
        skip() {
          return (
            this.mode !== 'testator' || !this.value || !this.bookingTestatorId
          );
        },
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all',
        error(error) {
          console.error('Error fetching testator:', error);
        },
      },
      advisorContact: {
        query: USER_CONTACT_QUERY,
        variables() {
          return {
            id: this.userId,
          };
        },
        skip() {
          return this.mode !== 'advisor' || !this.value || !this.userId;
        },
        update: data => data.user,
        fetchPolicy: 'cache-and-network',
        error(error) {
          console.error('Error fetching adviser details:', error);
        },
      },
    },
  };
</script>

<style scoped lang="scss">
  .booking-dialog-title {
    align-items: flex-start;
    word-break: normal;

    h2 {
      font-size: 22px;
      font-weight: 600;
      line-height: 28px;
    }
  }

  .booking-dialog-subtitle {
    font-size: 15px;
    font-weight: 400;
    color: var(--v-font-color-base);
  }
</style>
